import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { createClient } from "@west-tennessee-consulting/ui";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { logger } from "@/lib/logger";
import { ForecastMetrics, Deal, RevenueTarget } from "@/lib/types/database/deals";
import { aggregateForecast } from "@/lib/dashboard/metrics";
import { computeRenewalsDueSoon } from "@/lib/dashboard/metrics/forecast";
import { supabaseLostStagesNotInList } from "@/lib/domain/crm-entity-relationships";
import { fetchAllSupabaseRows } from "@/lib/hooks/api/dashboard/paginate";

// ============================================================================
// CEO FORECAST METRICS HOOK
// ============================================================================
//
// PURPOSE: Calculate 90-day forecast metrics for CEO dashboard
//
// METRICS:
// - Weighted Pipeline: Sum of (deal amount × close_probability ÷ 100)
// - Committed Revenue: Sum of deal amounts in Contract/Negotiation stages
// - Recurring MRR: Sum of monthly_amount for recurring deals
// - Revenue Gap to Target: target_amount - (weighted + committed + MRR)
//
// ============================================================================

interface UseCeoForecastMetricsOptions {
  dateFrom?: string; // ISO date
  dateTo?: string; // ISO date
  enabled?: boolean;
}

// Rental deals are excluded entirely from CEO forecast — they never count
// toward weighted pipeline, committed revenue, MRR, or any deal list here.
const RENTAL_DEAL_TAGS = new Set(["rental", "rentals"]);

function isRentalDeal(deal: Deal): boolean {
  return (deal.tags ?? []).some(
    (tag) => typeof tag === "string" && RENTAL_DEAL_TAGS.has(tag.trim().toLowerCase())
  );
}

export function useCeoForecastMetrics(options: UseCeoForecastMetricsOptions = {}) {
  const { isAuthenticated } = useUniversalAuth();
  const supabase = createClient();
  const { enabled = true, dateFrom, dateTo } = options;

  // Validate date range
  if (dateFrom && dateTo) {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    if (from > to) {
      throw new Error("Start date must be before end date");
    }
  }

  const { data: deals, isLoading: dealsLoading, error: dealsError } = useQuery({
    queryKey: ["ceo-forecast-deals", dateFrom, dateTo],
    queryFn: async (): Promise<Deal[]> => {
      if (!isAuthenticated) {
        throw new Error("Authentication required");
      }

      // Fetch deals that count toward the period projection: WON + delivery deals
      // (work already done — count in full) AND active pipeline deals. Only LOST
      // deals are excluded. See lib/dashboard/metrics/forecast.ts for the rationale.
      //
      // Page to completion: PostgREST caps a single response near 1000 rows, which
      // would silently truncate the aggregate math past that. fetchAllSupabaseRows
      // loops .range() until a short page. The builder MUST be constructed fresh per
      // call (the Supabase query builder is single-use).
      let data: Deal[];
      try {
        data = await fetchAllSupabaseRows<Deal>(({ from, to }) => {
          let query = supabase
            .from("deals")
            .select("*")
            .not("stage", "in", supabaseLostStagesNotInList());

          // Apply date filtering if provided
          if (dateFrom) {
            query = query.gte("close_date", dateFrom);
          }
          if (dateTo) {
            query = query.lte("close_date", dateTo);
          }

          // id is the unique tiebreak: without it, deals sharing a created_at can
          // straddle a page boundary and be dropped or double-counted.
          return query
            .order("created_at", { ascending: false })
            .order("id", { ascending: true })
            .range(from, to);
        });
      } catch (error) {
        logger.error("Error fetching deals", error);
        throw error;
      }

      return data.filter((deal) => !isRentalDeal(deal));
    },
    enabled: isAuthenticated && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute for critical forecast metrics
  });

  const { data: revenueTarget, isLoading: targetLoading } = useQuery({
    queryKey: ["ceo-revenue-target"],
    queryFn: async (): Promise<RevenueTarget | null> => {
      if (!isAuthenticated) {
        throw new Error("Authentication required");
      }

      // Get the latest revenue target
      const { data, error } = await supabase
        .from("revenue_targets")
        .select("*")
        .order("period_start", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 = no rows returned
        logger.error("Error fetching revenue target", error);
      }

      return (data as RevenueTarget) || null;
    },
    enabled: isAuthenticated && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes for revenue target (less critical)
  });

  // Calculate metrics using useMemo to prevent unnecessary recalculations
  const metrics = useMemo((): ForecastMetrics => {
    if (!deals) {
      return {
        weightedPipeline: 0,
        committedRevenue: 0,
        recurringMRR: 0,
        revenueGapToTarget: 0,
        currency: "USD",
      };
    }

    // All forecast math lives in lib/dashboard/metrics/forecast.ts so the card
    // faces and the drilldown modal share one definition (they cannot diverge).
    const aggregate = aggregateForecast(deals);

    const totalForecastedRevenue =
      aggregate.weightedPipeline + aggregate.committedRevenue + aggregate.recurringMRR;
    const revenueGapToTarget = revenueTarget
      ? Math.max(0, revenueTarget.target_amount - totalForecastedRevenue)
      : 0;

    return {
      weightedPipeline: aggregate.weightedPipeline,
      committedRevenue: aggregate.committedRevenue,
      recurringMRR: aggregate.recurringMRR,
      revenueGapToTarget: Math.round(revenueGapToTarget * 100) / 100,
      revenueTarget: revenueTarget?.target_amount,
      currency: revenueTarget?.currency || "USD",
      // Confidence indicators for data quality — active deals missing a probability
      // are excluded from the weighted $ (never imputed) and surfaced as a nudge.
      dealsWithProbability: aggregate.activeDealsWithProbability,
      dealsWithoutProbability: aggregate.activeDealsNeedingProbability,
      probabilityConfidence: aggregate.activeDealsNeedingProbability === 0 ? "high" : "medium",
      // Won recurring contracts approaching end of term (derived renewal date).
      renewalsDueSoon: computeRenewalsDueSoon(deals, new Date()),
    };
  }, [deals, revenueTarget]);

  return {
    metrics,
    isLoading: dealsLoading || targetLoading,
    error: dealsError,
    deals,
    revenueTarget,
  };
}
